// File: frontend/src/pages/AdminDashboardPage.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { useAuthContext } from '../contexts/AuthContext'
import { NavLink } from 'react-router-dom'
import * as adminService from '../services/adminService'
import { getArcanos, deleteArcano, updateArcano } from '../services/arcanosService'
import {
  ENABLE_ADMIN_CONVERSATION_VIEW,
  ARCANA_ENABLED,
} from '../constants/featureFlags'
// Added FiAward for the new Features link
import {
  FiTrash2,
  FiUsers,
  FiMessageSquare,
  FiAlertTriangle,
  FiLoader,
  FiRefreshCw,
  FiX,
  FiEye,
  FiDownload,
  FiHash,
  FiList,
  FiBarChart2,
  FiDatabase,
  FiMenu,
  FiLogOut,
  FiShield,
  FiChevronUp,
  FiChevronDown,
  FiSearch,
  FiInbox,
  FiAward,
  FiEdit3,
  FiSave,
  FiImage,
  FiVideo,
} from 'react-icons/fi'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import './AdminDashboardPage.css'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material'

const InsightCard = ({ title, value, icon }) => (
  <div className="admin-insight-card">
    <div className="icon">{icon}</div>
    <p>{title}</p>
    <span>{value}</span>
  </div>
)

const SortIcon = ({ direction }) => {
  if (!direction) return null
  return direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />
}

// The user object is passed to check if they are a superadmin for the new link
const AdminSidebar = ({ user, logout }) => (
  <>
    <nav className="admin-sidebar-nav">
      <NavLink to="/admin/dashboard" className="admin-nav-link" end>
        <FiBarChart2 size={20} /> Dashboard
      </NavLink>
      <NavLink to="/admin/kb" className="admin-nav-link">
        <FiDatabase size={20} /> Knowledge
      </NavLink>
      {/* NEW LINK - Only show to superadmins */}
      {user?.roles?.includes(
        process.env.REACT_APP_SUPERADMIN_ROLE_NAME || 'superadmin'
      ) && (
        <NavLink to="/admin/features" className="admin-nav-link">
          <FiAward size={20} /> Features
        </NavLink>
      )}
      <NavLink to="/admin/privacy" className="admin-nav-link">
        <FiShield size={20} /> Privacy
      </NavLink>
      <NavLink to="/admin/feedback" className="admin-nav-link">
        <FiInbox size={20} /> Feedback
      </NavLink>
      <NavLink to="/chat" className="admin-nav-link">
        <FiMessageSquare size={20} /> Chat
      </NavLink>
    </nav>
    <div className="admin-sidebar-footer">
      <div className="admin-user-info">
        {user && (
          <p className="admin-user-display-name" title={user.display_name}>
            {user.display_name || user.first_name || 'Utente'}
          </p>
        )}
      </div>
      <div className="admin-sidebar-footer-actions">
        <button onClick={logout} className="admin-icon-button small" title="Esci">
          <FiLogOut />
        </button>
      </div>
    </div>
  </>
)

const AdminDashboardPage = () => {
  const { token, user, logout } = useAuthContext()
  // ... (rest of the component state and logic remains the same)

  const [users, setUsers] = useState([])
  const [conversations, setConversations] = useState([])
  const [globalStats, setGlobalStats] = useState(null)

  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [userCurrentPage, setUserCurrentPage] = useState(1)
  const [userSortConfig, setUserSortConfig] = useState({
    key: 'last_login_at',
    direction: 'desc',
  })
  const [userDateFilter, setUserDateFilter] = useState('')
  const [usersTotalCount, setUsersTotalCount] = useState(0)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [usersError, setUsersError] = useState('')

  const [conversationSearchTerm, setConversationSearchTerm] = useState('')
  const [conversationCurrentPage, setConversationCurrentPage] = useState(1)
  const [conversationSortConfig, setConversationSortConfig] = useState({
    key: 'updated_at',
    direction: 'desc',
  })
  const [conversationDateFilter, setConversationDateFilter] = useState('')
  const [conversationsTotalCount, setConversationsTotalCount] = useState(0)
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [conversationsError, setConversationsError] = useState('')

  const [loadingGlobalStats, setLoadingGlobalStats] = useState(false)
  const [globalStatsError, setGlobalStatsError] = useState('')

  // Generation stats state
  const [generationStats, setGenerationStats] = useState(null)
  const [recentGenerations, setRecentGenerations] = useState([])
  const [loadingGenerationStats, setLoadingGenerationStats] = useState(false)
  const [generationStatsError, setGenerationStatsError] = useState('')

  // Arcano management state
  const [arcanos, setArcanos] = useState([])
  const [loadingArcanos, setLoadingArcanos] = useState(false)
  const [arcanosError, setArcanosError] = useState('')

  // Arcano editing state
  const [editingArcano, setEditingArcano] = useState(null)
  const [updatingArcano, setUpdatingArcano] = useState(false)

  const [error, setError] = useState('')

  const [isExporting, setIsExporting] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [isSidebarOpen, setSidebarOpen] = useState(false)

  const isAdmin = user?.roles?.includes(
    process.env.REACT_APP_ADMIN_ROLE_NAME || 'admin'
  )
  const USERS_PER_PAGE = 10
  const CONVERSATIONS_PER_PAGE = 10

  const fetchUsersData = useCallback(async () => {
    if (!token || !isAdmin) return
    setLoadingUsers(true)
    setUsersError('')
    try {
      const usersData = await adminService.getAllUsersStats(token, {
        search: userSearchTerm,
        page: userCurrentPage,
        limit: USERS_PER_PAGE,
        sortBy: userSortConfig.key,
        sortDir: userSortConfig.direction,
        lastLoginFilter: userDateFilter,
      })
      setUsers(usersData.users || [])
      setUsersTotalCount(usersData.total_count || 0)
    } catch (err) {
      console.error('Failed to fetch users stats:', err)
      setUsersError(
        err.response?.data?.detail || 'Errore nel caricamento statistiche utenti.'
      )
    } finally {
      setLoadingUsers(false)
    }
  }, [token, isAdmin, userSearchTerm, userCurrentPage, userSortConfig, userDateFilter])

  useEffect(() => {
    if (isAdmin) fetchUsersData()
  }, [fetchUsersData, isAdmin])

  const fetchConversationsData = useCallback(async () => {
    if (!token || !isAdmin || !ENABLE_ADMIN_CONVERSATION_VIEW) return
    setLoadingConversations(true)
    setConversationsError('')
    try {
      const convosData = await adminService.getAllConversations(token, {
        search: conversationSearchTerm,
        page: conversationCurrentPage,
        limit: CONVERSATIONS_PER_PAGE,
        sortBy: conversationSortConfig.key,
        sortDir: conversationSortConfig.direction,
        dateFilter: conversationDateFilter,
      })
      setConversations(convosData.conversations || [])
      setConversationsTotalCount(convosData.total_count || 0)
    } catch (err) {
      console.error('Failed to fetch conversations:', err)
      setConversationsError(
        err.response?.data?.detail || 'Errore nel caricamento delle conversazioni.'
      )
    } finally {
      setLoadingConversations(false)
    }
  }, [
    token,
    isAdmin,
    conversationSearchTerm,
    conversationCurrentPage,
    conversationSortConfig,
    conversationDateFilter,
  ])

  useEffect(() => {
    if (isAdmin && ENABLE_ADMIN_CONVERSATION_VIEW) fetchConversationsData()
  }, [fetchConversationsData, isAdmin])

  const fetchGlobalStatsData = useCallback(async () => {
    if (!token || !isAdmin) return
    setLoadingGlobalStats(true)
    setGlobalStatsError('')
    try {
      const globalStatsData = await adminService.getGlobalStats(token)
      setGlobalStats(globalStatsData)
    } catch (err) {
      console.error('Failed to fetch global stats:', err)
      setGlobalStatsError(
        err.response?.data?.detail ||
          'Errore nel caricamento delle statistiche globali.'
      )
    } finally {
      setLoadingGlobalStats(false)
    }
  }, [token, isAdmin])

  useEffect(() => {
    if (isAdmin) fetchGlobalStatsData()
  }, [fetchGlobalStatsData, isAdmin])

  const fetchGenerationStatsData = useCallback(async () => {
    if (!token || !isAdmin) return
    setLoadingGenerationStats(true)
    setGenerationStatsError('')
    try {
      const [statsData, recentData] = await Promise.all([
        adminService.getGenerationStats(token, 24, 5),
        adminService.getRecentGenerations(token, 10, 60),
      ])
      setGenerationStats(statsData)
      setRecentGenerations(recentData)
    } catch (err) {
      console.error('Failed to fetch generation stats:', err)
      setGenerationStatsError(
        err.response?.data?.detail ||
          'Errore nel caricamento delle statistiche di generazione.'
      )
    } finally {
      setLoadingGenerationStats(false)
    }
  }, [token, isAdmin])

  useEffect(() => {
    if (isAdmin) fetchGenerationStatsData()
  }, [fetchGenerationStatsData, isAdmin])

  const handleUserSearchChange = event => {
    setUserSearchTerm(event.target.value)
    setUserCurrentPage(1)
  }

  const handleUserDateFilterChange = event => {
    setUserDateFilter(event.target.value)
    setUserCurrentPage(1)
  }

  const handleUserSort = key => {
    setUserSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        }
      }
      return { key, direction: 'asc' }
    })
    setUserCurrentPage(1)
  }

  const handleUserPageChange = newPage => {
    if (newPage > 0 && newPage <= Math.ceil(usersTotalCount / USERS_PER_PAGE)) {
      setUserCurrentPage(newPage)
    }
  }

  const handleConversationSearchChange = event => {
    setConversationSearchTerm(event.target.value)
    setConversationCurrentPage(1)
  }

  const handleConversationDateFilterChange = event => {
    setConversationDateFilter(event.target.value)
    setConversationCurrentPage(1)
  }

  const handleConversationSort = key => {
    setConversationSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc',
        }
      }
      return { key, direction: 'asc' }
    })
    setConversationCurrentPage(1)
  }

  const handleConversationPageChange = newPage => {
    if (
      newPage > 0 &&
      newPage <= Math.ceil(conversationsTotalCount / CONVERSATIONS_PER_PAGE)
    ) {
      setConversationCurrentPage(newPage)
    }
  }

  const handleExport = async () => {
    if (!token) return
    setIsExporting(true)
    setError('')
    try {
      await adminService.exportAllConversations(token)
    } catch (err) {
      console.error('Export failed:', err)
      setError(err.response?.data?.detail || 'Esportazione dei dati fallita.')
    } finally {
      setIsExporting(false)
    }
  }

  const handleViewConversation = async conversationId => {
    if (!token) return
    setIsModalOpen(true)
    setLoadingModal(true)
    setSelectedConversation(null)
    try {
      const convoDetails = await adminService.getConversationDetails(
        token,
        conversationId
      )
      setSelectedConversation(convoDetails)
    } catch (err) {
      setError('Impossibile caricare i dettagli della conversazione.')
    } finally {
      setLoadingModal(false)
    }
  }

  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatNumber = num => (num || 0).toLocaleString('it-IT')

  const totalUserPages = Math.ceil(usersTotalCount / USERS_PER_PAGE)
  const totalConversationPages = Math.ceil(
    conversationsTotalCount / CONVERSATIONS_PER_PAGE
  )

  // Arcano management functions
  const fetchArcanosData = useCallback(async () => {
    if (!token || !isAdmin || !ARCANA_ENABLED) return
    setLoadingArcanos(true)
    setArcanosError('')
    try {
      const arcanosData = await getArcanos(token)
      setArcanos(arcanosData)
    } catch (err) {
      console.error('Failed to fetch arcanos:', err)
      setArcanosError(
        err.response?.data?.detail || 'Errore nel caricamento degli Arcani.'
      )
    } finally {
      setLoadingArcanos(false)
    }
  }, [token, isAdmin])

  const handleDeleteArcano = async arcanaId => {
    if (!token || !window.confirm('Sei sicuro di voler eliminare questo Arcano?'))
      return

    try {
      await deleteArcano(token, arcanaId)
      await fetchArcanosData()
    } catch (err) {
      console.error('Failed to delete arcano:', err)
      setError(err.response?.data?.detail || "Errore nell'eliminazione dell'Arcano.")
    }
  }

  // Available teams for Arcano assignment
  const AVAILABLE_TEAMS = ['SFRE', 'SFE', 'SFCM', 'SFS', 'SFSE']

  const handleEditArcano = arcano => {
    setEditingArcano({
      ...arcano,
      allowed_teams: arcano.allowed_teams || [],
    })
  }

  const handleUpdateArcanoTeams = async arcanoData => {
    if (!token || !editingArcano) return

    setUpdatingArcano(true)
    try {
      await updateArcano(token, editingArcano.arcana_id, {
        allowed_teams:
          arcanoData.allowed_teams.length > 0 ? arcanoData.allowed_teams : null,
      })
      await fetchArcanosData()
      setEditingArcano(null)
    } catch (err) {
      console.error('Failed to update arcano teams:', err)
      setArcanosError(
        err.response?.data?.detail || "Errore nell'aggiornamento dei team dell'Arcano."
      )
    } finally {
      setUpdatingArcano(false)
    }
  }

  useEffect(() => {
    if (isAdmin && ARCANA_ENABLED) fetchArcanosData()
  }, [fetchArcanosData, isAdmin])

  // Arcano Team Management Modal Component
  const ArcanoTeamModal = () => {
    const [formData, setFormData] = useState({
      name: editingArcano?.name || '',
      arcana_id: editingArcano?.arcana_id || '',
      description: editingArcano?.description || '',
      allowed_teams: editingArcano?.allowed_teams || [],
    })

    useEffect(() => {
      if (editingArcano) {
        setFormData({
          name: editingArcano.name || '',
          arcana_id: editingArcano.arcana_id || '',
          description: editingArcano.description || '',
          allowed_teams: editingArcano.allowed_teams || [],
        })
      }
    }, [])

    const handleTeamToggle = team => {
      setFormData(prev => ({
        ...prev,
        allowed_teams: prev.allowed_teams.includes(team)
          ? prev.allowed_teams.filter(t => t !== team)
          : [...prev.allowed_teams, team],
      }))
    }

    const handleSubmit = e => {
      e.preventDefault()
      handleUpdateArcanoTeams(formData)
    }

    const handleClose = () => {
      setEditingArcano(null)
    }

    return (
      <Dialog
        open={!!editingArcano}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--shadow-sm)',
            overflow: 'hidden',
          },
        }}
      >
        {/* HEADER */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            fontWeight: 600,
            fontSize: '1.1rem',
            color: 'var(--text-primary)',
          }}
        >
          Modifica Team Arcano
          <IconButton onClick={handleClose} size="small">
            <FiX />
          </IconButton>
        </DialogTitle>

        {/* CONTENT */}
        <form onSubmit={handleSubmit}>
          <DialogContent
            dividers
            sx={{
              border: 'none',
              textAlign: 'center',
              pt: 3,
              pb: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Team Consentiti:
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              {AVAILABLE_TEAMS.map(team => (
                <FormControlLabel
                  key={team}
                  control={
                    <Checkbox
                      checked={formData.allowed_teams.includes(team)}
                      onChange={() => handleTeamToggle(team)}
                      sx={{
                        color: 'var(--brand-500)',
                        '&.Mui-checked': {
                          color: 'var(--brand-500)',
                        },
                      }}
                    />
                  }
                  label={team}
                />
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary">
              Lascia vuoto per rendere l&apos;Arcano pubblico (accessibile a tutti)
            </Typography>
          </DialogContent>

          {/* ACTIONS */}
          <DialogActions
            sx={{
              borderTop: '1px solid rgba(0,0,0,0.06)',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': { background: 'rgba(0,0,0,0.04)' },
              }}
              disabled={updatingArcano}
            >
              Annulla
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={updatingArcano}
              startIcon={updatingArcano ? <FiLoader /> : <FiSave />}
              sx={{
                background:
                  'linear-gradient(135deg, var(--brand-500) 0%, var(--brand-active) 100%)',
                color: 'var(--text-on-accent)',
                boxShadow: 'var(--shadow-sm)',
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  background:
                    'linear-gradient(135deg, var(--brand-active) 0%, var(--brand-500) 100%)',
                },
              }}
            >
              {updatingArcano ? 'Salvando...' : 'Salva Modifiche'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    )
  }

  if (!isAdmin) {
    return (
      <div className="admin-page-container">
        <main
          className="admin-main-content"
          style={{ margin: 'auto', maxWidth: '600px' }}
        >
          <div className="admin-card" style={{ textAlign: 'center' }}>
            <FiAlertTriangle size={48} className="danger" />
            <h2>Accesso Negato</h2>
            <p>Non hai i permessi per visualizzare questa pagina.</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      {isSidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar-header">
          <img
            src="/franca-logo.png"
            alt="F.R.A.N.C.A."
            className="admin-sidebar-logo"
          />
        </div>
        <AdminSidebar user={user} logout={logout} />
      </aside>

      {/* ... (rest of the component JSX remains the same) ... */}
      <main className="admin-main-content">
        <header className="admin-page-header">
          <button
            className="admin-button mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu />
          </button>
          <h1>
            <FiBarChart2 /> Admin Dashboard
          </h1>
          <div className="admin-page-header-actions">
            <button
              onClick={() => {
                fetchUsersData()
                if (ENABLE_ADMIN_CONVERSATION_VIEW) fetchConversationsData()
                fetchGlobalStatsData()
                if (ARCANA_ENABLED) fetchArcanosData()
              }}
              disabled={
                loadingUsers ||
                (ENABLE_ADMIN_CONVERSATION_VIEW && loadingConversations) ||
                loadingGlobalStats ||
                (ARCANA_ENABLED && loadingArcanos)
              }
              className="admin-button secondary"
            >
              <FiRefreshCw
                className={
                  loadingUsers ||
                (ENABLE_ADMIN_CONVERSATION_VIEW && loadingConversations) ||
                loadingGlobalStats
                    ? 'spinner-icon'
                    : ''
                }
              />
              Aggiorna Tutto
            </button>
            {ENABLE_ADMIN_CONVERSATION_VIEW && (
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="admin-button primary"
              >
                {isExporting ? <FiLoader className="spinner-icon" /> : <FiDownload />}
                Esporta
              </button>
            )}
          </div>
        </header>

        {error && <div className="admin-error-message">{error}</div>}
        {globalStatsError && (
          <div className="admin-error-message">{globalStatsError}</div>
        )}

        <section className="admin-card">
          <h2>Statistiche Globali</h2>
          {loadingGlobalStats ? (
            <div className="admin-loading-state">
              <FiLoader className="spinner-icon" /> Caricamento...
            </div>
          ) : globalStats ? (
            <div className="admin-insight-grid">
              <InsightCard
                title="Utenti Totali"
                value={formatNumber(globalStats.total_users)}
                icon={<FiUsers />}
              />
              <InsightCard
                title="Conversazioni"
                value={formatNumber(globalStats.total_conversations)}
                icon={<FiMessageSquare />}
              />
              <InsightCard
                title="Messaggi"
                value={formatNumber(globalStats.total_messages)}
                icon={<FiList />}
              />
              <InsightCard
                title="Token"
                value={formatNumber(globalStats.total_tokens)}
                icon={<FiHash />}
              />
            </div>
          ) : (
            !globalStatsError && <p>Nessuna statistica globale disponibile.</p>
          )}
        </section>

        {/* Generation Stats Section */}
        <section className="admin-card">
          <div className="admin-section-header">
            <h2>
              <FiBarChart2 /> Statistiche Generazione (Ultime 24h)
            </h2>
            <button
              onClick={fetchGenerationStatsData}
              disabled={loadingGenerationStats}
              className="admin-button secondary small"
              title="Aggiorna statistiche generazione"
            >
              <FiRefreshCw className={loadingGenerationStats ? 'spinner-icon' : ''} />
              Aggiorna
            </button>
          </div>

          {generationStatsError && (
            <div className="admin-error-message">{generationStatsError}</div>
          )}

          {loadingGenerationStats ? (
            <div className="admin-loading-state">
              <FiLoader className="spinner-icon" /> Caricamento statistiche
              generazione...
            </div>
          ) : generationStats ? (
            <>
              <div className="admin-insight-grid">
                <InsightCard
                  title="Immagini Generate"
                  value={formatNumber(generationStats.total_images || 0)}
                  icon={<FiImage />}
                />
                <InsightCard
                  title="Video Generati"
                  value={formatNumber(generationStats.total_videos || 0)}
                  icon={<FiVideo />}
                />
                <InsightCard
                  title="Utenti Attivi"
                  value={formatNumber(generationStats.unique_users || 0)}
                  icon={<FiUsers />}
                />
                <InsightCard
                  title="Totale Generazioni"
                  value={formatNumber(
                    (generationStats.total_images || 0) +
                      (generationStats.total_videos || 0)
                  )}
                  icon={<FiBarChart2 />}
                />
              </div>

              {/* Recent Generations */}
              {recentGenerations && recentGenerations.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ marginBottom: '12px' }}>Recenti (Ultima ora)</h3>
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Utente</th>
                          <th>Modello</th>
                          <th>Prompt</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentGenerations.map((gen, index) => (
                          <tr key={index}>
                            <td>
                              <span
                                className={`admin-badge ${
                                  gen.type === 'image' ? 'info' : 'success'
                                }`}
                              >
                                {gen.type === 'image' ? 'Immagine' : 'Video'}
                              </span>
                            </td>
                            <td>{gen.user_email || 'N/A'}</td>
                            <td>
                              <code>{gen.model || 'N/A'}</code>
                            </td>
                            <td
                              style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {gen.prompt && gen.prompt.length > 60
                                ? `${gen.prompt.substring(0, 60)}...`
                                : gen.prompt || 'N/A'}
                            </td>
                            <td>{formatDate(gen.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Users */}
              {generationStats.top_users && generationStats.top_users.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ marginBottom: '12px' }}>Top 5 Utenti</h3>
                  <div className="admin-table-container">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Utente</th>
                          <th>Immagini</th>
                          <th>Video</th>
                          <th>Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generationStats.top_users.map((user, index) => (
                          <tr key={index}>
                            <td>{user.user_email || 'N/A'}</td>
                            <td>{formatNumber(user.image_count || 0)}</td>
                            <td>{formatNumber(user.video_count || 0)}</td>
                            <td>
                              <strong>{formatNumber(user.total_count || 0)}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            !generationStatsError && (
              <p>Nessuna statistica di generazione disponibile.</p>
            )
          )}
        </section>

        {/* Arcano Management Section - hidden when ARCANA_ENABLED=false */}
        {ARCANA_ENABLED && (
        <section className="admin-card">
          <div className="admin-section-header">
            <h2>
              <FiAward /> Gestione Arcani
            </h2>
          </div>

          {arcanosError && <div className="admin-error-message">{arcanosError}</div>}

          {loadingArcanos ? (
            <div className="admin-loading-state">
              <FiLoader className="spinner-icon" /> Caricamento Arcani...
            </div>
          ) : arcanos.length > 0 ? (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>ID</th>
                    <th>Descrizione</th>
                    <th>Team Consentiti</th>
                    <th>Creato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {arcanos.map(arcano => (
                    <tr key={arcano.arcana_id}>
                      <td>{arcano.name}</td>
                      <td>
                        <code>{arcano.arcana_id}</code>
                      </td>
                      <td>{arcano.description || '-'}</td>
                      <td>
                        {arcano.allowed_teams && arcano.allowed_teams.length > 0 ? (
                          <span className="admin-team-badges">
                            {arcano.allowed_teams.map((team, index) => (
                              <span key={index} className="admin-team-badge">
                                {team}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="admin-public-badge">Pubblico</span>
                        )}
                      </td>
                      <td>{formatDate(arcano.created_at)}</td>
                      <td>
                        <div className="admin-button-group">
                          <button
                            onClick={() => handleEditArcano(arcano)}
                            className="admin-button secondary small"
                            title="Modifica Team"
                          >
                            <FiEdit3 />
                          </button>
                          <button
                            onClick={() => handleDeleteArcano(arcano.arcana_id)}
                            className="admin-button danger small"
                            title="Elimina Arcano"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Nessun Arcano disponibile.</p>
          )}
        </section>
        )}

        <section className="admin-card">
          <div className="admin-section-header">
            <h2>
              <FiUsers /> Statistiche Utenti
            </h2>
            <button
              onClick={fetchUsersData}
              disabled={loadingUsers}
              className="admin-button secondary small"
              title="Aggiorna dati utenti"
            >
              <FiRefreshCw className={loadingUsers ? 'spinner-icon' : ''} />
              Aggiorna Utenti
            </button>
          </div>
          <div className="admin-filters-bar">
            <div className="admin-search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Cerca per nome o email..."
                value={userSearchTerm}
                onChange={handleUserSearchChange}
              />
            </div>
            <select
              value={userDateFilter}
              onChange={handleUserDateFilterChange}
              className="admin-select"
            >
              <option value="">Ultimo Login: Qualsiasi</option>
              <option value="2d">Ultime 48 ore</option>
              <option value="7d">Ultimi 7 giorni</option>
              <option value="30d">Ultimi 30 giorni</option>
            </select>
          </div>
          {usersError && (
            <div className="admin-error-message" style={{ marginTop: '10px' }}>
              {usersError}
            </div>
          )}
          <div className={`admin-table-container ${loadingUsers ? 'is-loading' : ''}`}>
            <table>
              <thead>
                <tr>
                  <th
                    onClick={() => handleUserSort('sfre_email')}
                    className="sortable-header"
                  >
                    Email Utente{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'sfre_email'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('display_name')}
                    className="sortable-header"
                  >
                    Nome{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'display_name'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('team')}
                    className="sortable-header"
                  >
                    Team{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'team' ? userSortConfig.direction : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('total_conversations')}
                    className="sortable-header"
                  >
                    # Conv.{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'total_conversations'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('total_messages')}
                    className="sortable-header"
                  >
                    # Msg.{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'total_messages'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('total_tokens')}
                    className="sortable-header"
                  >
                    Token Totali{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'total_tokens'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleUserSort('last_login_at')}
                    className="sortable-header"
                  >
                    Ultimo Login{' '}
                    <SortIcon
                      direction={
                        userSortConfig.key === 'last_login_at'
                          ? userSortConfig.direction
                          : null
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers && users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="admin-loading-state">
                      <FiLoader className="spinner-icon" /> Caricamento utenti...
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map(u => (
                    <tr key={u.id || u.sfre_email}>
                      <td>{u.sfre_email}</td>
                      <td>{u.display_name || 'N/A'}</td>
                      <td>
                        {u.team ? (
                          <span className="admin-team-badge">{u.team}</span>
                        ) : (
                          <span className="admin-no-team">Nessun team</span>
                        )}
                      </td>
                      <td>{formatNumber(u.total_conversations)}</td>
                      <td>{formatNumber(u.total_messages)}</td>
                      <td>{formatNumber(u.total_tokens)}</td>
                      <td>{formatDate(u.last_login_at)}</td>
                    </tr>
                  ))
                ) : (
                  !usersError && (
                    <tr>
                      <td colSpan="7" className="admin-no-data">
                        Nessun utente trovato.
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          {usersTotalCount > 0 && !usersError && (
            <div className="admin-pagination-controls">
              <button
                onClick={() => handleUserPageChange(userCurrentPage - 1)}
                disabled={userCurrentPage === 1 || loadingUsers}
              >
                Precedente
              </button>
              <span>
                Pagina {userCurrentPage} di {totalUserPages}
              </span>
              <button
                onClick={() => handleUserPageChange(userCurrentPage + 1)}
                disabled={userCurrentPage === totalUserPages || loadingUsers}
              >
                Successiva
              </button>
            </div>
          )}
        </section>

        {ENABLE_ADMIN_CONVERSATION_VIEW && (
        <section className="admin-card">
          <h2>
            <FiMessageSquare /> Ultime Conversazioni
          </h2>
          <div className="admin-filters-bar">
            <div className="admin-search-input">
              <FiSearch />
              <input
                type="text"
                placeholder="Cerca titolo, email o nel contenuto..."
                value={conversationSearchTerm}
                onChange={handleConversationSearchChange}
              />
            </div>
            <select
              value={conversationDateFilter}
              onChange={handleConversationDateFilterChange}
              className="admin-select"
            >
              <option value="">Data Aggiornamento: Qualsiasi</option>
              <option value="2d">Ultime 48 ore</option>
              <option value="7d">Ultimi 7 giorni</option>
              <option value="30d">Ultimi 30 giorni</option>
            </select>
          </div>
          {conversationsError && (
            <div className="admin-error-message" style={{ marginTop: '10px' }}>
              {conversationsError}
            </div>
          )}
          <div
            className={`admin-table-container ${
              loadingConversations ? 'is-loading' : ''
            }`}
          >
            <table>
              <thead>
                <tr>
                  <th
                    onClick={() => handleConversationSort('title')}
                    className="sortable-header"
                  >
                    Titolo{' '}
                    <SortIcon
                      direction={
                        conversationSortConfig.key === 'title'
                          ? conversationSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleConversationSort('user_email')}
                    className="sortable-header"
                  >
                    Email Utente{' '}
                    <SortIcon
                      direction={
                        conversationSortConfig.key === 'user_email'
                          ? conversationSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleConversationSort('message_count')}
                    className="sortable-header"
                  >
                    # Msg.{' '}
                    <SortIcon
                      direction={
                        conversationSortConfig.key === 'message_count'
                          ? conversationSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleConversationSort('total_tokens')}
                    className="sortable-header"
                  >
                    Token{' '}
                    <SortIcon
                      direction={
                        conversationSortConfig.key === 'total_tokens'
                          ? conversationSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th
                    onClick={() => handleConversationSort('updated_at')}
                    className="sortable-header"
                  >
                    Aggiornata{' '}
                    <SortIcon
                      direction={
                        conversationSortConfig.key === 'updated_at'
                          ? conversationSortConfig.direction
                          : null
                      }
                    />
                  </th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loadingConversations && conversations.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="admin-loading-state">
                      <FiLoader className="spinner-icon" /> Caricamento conversazioni...
                    </td>
                  </tr>
                ) : conversations.length > 0 ? (
                  conversations.map(c => (
                    <tr key={c.id}>
                      <td className="convo-title" title={c.title}>
                        {c.title}
                      </td>
                      <td>{c.user_email}</td>
                      <td>{formatNumber(c.message_count)}</td>
                      <td>{formatNumber(c.total_tokens)}</td>
                      <td>{formatDate(c.updated_at)}</td>
                      <td>
                        <button
                          onClick={() => handleViewConversation(c.id)}
                          className="admin-button link"
                          title="Visualizza chat"
                        >
                          <FiEye />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  !conversationsError && (
                    <tr>
                      <td colSpan="6" className="admin-no-data">
                        Nessuna conversazione trovata.
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          {conversationsTotalCount > 0 && !conversationsError && (
            <div className="admin-pagination-controls">
              <button
                onClick={() =>
                  handleConversationPageChange(conversationCurrentPage - 1)
                }
                disabled={conversationCurrentPage === 1 || loadingConversations}
              >
                Precedente
              </button>
              <span>
                Pagina {conversationCurrentPage} di {totalConversationPages}
              </span>
              <button
                onClick={() =>
                  handleConversationPageChange(conversationCurrentPage + 1)
                }
                disabled={
                  conversationCurrentPage === totalConversationPages ||
                  loadingConversations
                }
              >
                Successiva
              </button>
            </div>
          )}
        </section>
        )}
      </main>

      {ENABLE_ADMIN_CONVERSATION_VIEW && isModalOpen && (
        <div className="admin-modal-overlay large">
          <div className="admin-modal-content">
            <div className="admin-modal-header">
              <h3>
                {loadingModal
                  ? 'Caricamento...'
                  : selectedConversation?.title || 'Dettagli Conversazione'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="admin-modal-close-button"
                title="Chiudi"
              >
                <FiX />
              </button>
            </div>

            {loadingModal ? (
              <div className="admin-full-area-loading">
                <FiLoader className="spinner-icon large" />
              </div>
            ) : selectedConversation ? (
              <div className="admin-modal-chat-container">
                {selectedConversation.messages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`message-bubble-wrapper ${msg.role}`}
                  >
                    <div className={`message-bubble ${msg.role}`}>
                      <span className="message-sender">
                        {msg.role === 'user' ? 'Utente' : 'F.R.A.N.C.A.'}
                      </span>
                      <div className="message-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                      <span className="message-timestamp">
                        {formatDate(msg.timestamp)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="admin-error-message" style={{ marginTop: '1rem' }}>
                {error || 'Impossibile caricare la conversazione.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Arcano Team Management Modal - only when ARCANA_ENABLED */}
      {ARCANA_ENABLED && <ArcanoTeamModal />}
    </div>
  )
}

export default AdminDashboardPage
